import { BaseProvider } from "./base.provider";

export interface WaitlistEntry {
  email: string;
  phone: string;
  created_at: string; // RFC 3339, always UTC
  referral_key?: string; // absent, not null, when there was no referral
  referred_by?: string;
  app_store_email: string | null;
  play_store_email: string | null;
  reached_out: boolean;
  notes?: string | null; // absent/null when never set, admin-only
}

export interface WaitlistUpdate {
  app_store_email?: string | null;
  play_store_email?: string | null;
  reached_out?: boolean;
  // undefined/omitted leaves notes untouched; "" is a real value that clears it.
  notes?: string | null;
}

// Shared allowlist and mechanics between the table (GET /v1/admin/waitlist)
// and the CSV export (GET /v1/admin/waitlist/export).
export type WaitlistSortBy =
  | "created_at"
  | "reached_out"
  | "email"
  | "phone"
  | "app_store_email"
  | "play_store_email"
  | "notes";

export interface Paginated<T> {
  data: T[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface WaitlistDailyCount {
  date: string;
  count: number;
}

export interface WaitlistHistoryResponse {
  total: number;
  history: WaitlistDailyCount[];
}

// Every row is an email plus a phone number, so nothing here may be persisted
// or forwarded anywhere — keep it in the React Query cache and nowhere else.
class WaitlistProvider extends BaseProvider {
  // Page size is fixed at 50 server-side and there's no total, so navigation
  // has to run off has_next. Without sortBy: reached_out ascending, then
  // created_at descending (today's default, unchanged). With sortBy: exactly
  // that field/direction, _id tiebreaker — reached_out is no longer forced in.
  list(
    page = 1,
    sortBy?: WaitlistSortBy,
    sortDesc?: boolean,
  ): Promise<Paginated<WaitlistEntry>> {
    const params = new URLSearchParams({ page: String(page) });
    if (sortBy) params.set("sort_by", sortBy);
    if (sortBy && sortDesc) params.set("sort_desc", "true");
    return this.request<Paginated<WaitlistEntry>>(
      `/v1/admin/waitlist?${params.toString()}`,
    );
  }

  // The whole list as one CSV, oldest first by default. Fetched rather than
  // linked so a 403 or 408 surfaces as a message instead of landing in
  // Downloads. minDate, when given, must be YYYY-MM-DD — the server 400s on
  // anything else, as it does for an invalid sortBy.
  exportCsv(
    minDate?: string,
    sortBy?: WaitlistSortBy,
    sortDesc?: boolean,
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (minDate) params.set("min_date", minDate);
    if (sortBy) params.set("sort_by", sortBy);
    if (sortDesc) params.set("sort_desc", "true");
    const query = params.toString();
    return this.requestBlob(
      `/v1/admin/waitlist/export${query ? `?${query}` : ""}`,
    );
  }

  // Partial update — only the fields present in `update` are changed.
  update(email: string, update: WaitlistUpdate): Promise<WaitlistEntry> {
    return this.request<WaitlistEntry>(
      `/v1/admin/waitlist/${encodeURIComponent(email)}`,
      { method: "PATCH", body: JSON.stringify(update) },
    );
  }

  // history is sparse per-day signup counts — days with zero signups are
  // omitted. total is the all-time waitlist total, independent of `days`.
  getHistory(days?: number): Promise<WaitlistHistoryResponse> {
    const query = days !== undefined ? `?days=${days}` : "";
    return this.request<WaitlistHistoryResponse>(
      `/v1/admin/waitlist/history${query}`,
    );
  }
}

export const waitlistProvider = new WaitlistProvider();
